import { AvailabilityDTO, AvailabilitySlot, DayOfWeek } from "@gymflow/types";
import { DaysOfWeekEnum, TIME_FORMAT } from "@gymflow/validation";
import { areIntervalsOverlapping } from "date-fns";
import { DateTime } from "luxon";

export type UIAvailabilityPerDay = {
  [key in DayOfWeek]: {
    active: boolean;
    blocks: { start: string; end: string | null }[];
  };
};
export type UIAvailability = {
  uiAvailabilityPerDay: UIAvailabilityPerDay;
};

export const generateApiAvailabilityPayload = ({
  uiAvailabilityPerDay,
}: UIAvailability) => {
  return {
    availabilitySlotList: Object.entries(uiAvailabilityPerDay)
      .filter(([_, day]) => day.active)
      .flatMap(([key, day]) =>
        day.blocks.map((block) => ({
          availabilityType: "STAFF",
          startTime: `${block.start}:00`,
          endTime: block.end === null ? null : `${block.end}:00`,
          weekDay: key as DayOfWeek,
        })),
      ),
  } as AvailabilityDTO;
};

export const generateUIAvailabilityPayload = (apiPayload: AvailabilityDTO) => {
  return {
    uiAvailabilityPerDay: apiPayload.availabilitySlotList.reduce(
      (acc, next) => {
        return {
          ...acc,
          [next.weekDay as DayOfWeek]: {
            active: true,
            blocks: [
              ...acc[next.weekDay as DayOfWeek].blocks,
              {
                start: next.startTime.split(":").slice(0, 2).join(":"),
                end:
                  next.endTime === null
                    ? null
                    : next.endTime.split(":").slice(0, 2).join(":"),
              },
            ],
          },
        };
      },
      DaysOfWeekEnum.reduce(
        (acc, next) => ({ ...acc, [next]: { active: false, blocks: [] } }),
        {},
      ) as UIAvailabilityPerDay,
    ),
  } as UIAvailability;
};

// Refactor this logic after refactoring on BE side.
// More in info in the comment from BE:
// https://linear.app/gymflow/issue/GYM-1131/[be]-improve-availability-on-staff-portalstaff-app#comment-f0ae42fa
// and
// https://linear.app/gymflow/issue/GYM-2233/allow-selecting-end-of-day-for-availability
export const getOverlapMapFromSlots = (
  slotList: AvailabilitySlot[],
): Record<DayOfWeek, boolean> => {
  const weekdays: DayOfWeek[] = [
    "MONDAY",
    "TUESDAY",
    "WEDNESDAY",
    "THURSDAY",
    "FRIDAY",
    "SATURDAY",
    "SUNDAY",
  ];

  // copy-past from previous variant, refactor it
  return weekdays.reduce(
    (prev, weekday) => {
      prev[weekday] = false;

      const weekdaySlots = slotList.filter((s) => s.weekDay === weekday);

      const hasOverlap = weekdaySlots.some((leftSlot, leftIdx) => {
        return weekdaySlots.some((rightSlot, rightIdx) => {
          if (leftIdx === rightIdx) {
            return false;
          }

          const leftStart = DateTime.fromFormat(
            leftSlot.startTime,
            TIME_FORMAT,
          );
          const leftEnd = DateTime.fromFormat(
            leftSlot.endTime === null ? "24:00:00" : leftSlot.endTime,
            TIME_FORMAT,
          );
          const rightStart = DateTime.fromFormat(
            rightSlot.startTime,
            TIME_FORMAT,
          );
          const rightEnd = DateTime.fromFormat(
            rightSlot.endTime === null ? "24:00:00" : rightSlot.endTime,
            TIME_FORMAT,
          );

          if (rightStart >= rightEnd) {
            return true;
          }

          return areIntervalsOverlapping(
            { start: leftStart.toJSDate(), end: leftEnd.toJSDate() },
            { start: rightStart.toJSDate(), end: rightEnd.toJSDate() },
          );
        });
      });

      prev[weekday] = hasOverlap;

      return prev;
    },
    {} as Record<DayOfWeek, boolean>,
  );
};
